
import { mixins } from "vue-class-component";
import { DateUtilsMixin } from "@/mixins/date-utils-mixin";
import { TableConfig } from '@/types';
import { Getter, State } from 'vuex-class';
import { VueGoodTable } from 'vue-good-table';
import ModalSection from '@/components/shared/ModalSection.vue';
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { DeceasedDonor } from '@/store/deceasedDonors/types';
import { GenericCodeValue } from '@/store/types';
import { AllocationRecipient, AllocationOfferTypeValues, AllocationResponse, AllocationOffer } from '@/store/allocations/types';

interface AllocationOfferHistoryPageState {
  title: string,
  allocationId: string,
  records: AllocationRecipient[]
}

@Component({
  components: {
    ModalSection,
    VueGoodTable
  }
})
export default class OfferHistoryModal extends mixins(DateUtilsMixin) {
  @State(state => state.deceasedDonors.selected) private donor!: DeceasedDonor;
  @State(state => state.currentUser) private currentUser!: any;

  @Getter('selectedAllocation', { namespace: 'allocations' }) private allocation!: any;
  @Getter('primaryOfferHistory', { namespace: 'allocations' }) private primaryOfferHistory!: AllocationRecipient[];
  @Getter('backupOfferHistory', { namespace: 'allocations' }) private backupOfferHistory!: AllocationRecipient[];
  @Getter('noOfferHistory', { namespace: 'allocations' }) private noOfferHistory!: AllocationRecipient[];
  @Getter('unfilteredOfferResponses', { namespace: 'lookups' }) private offerResponses!: GenericCodeValue[];
  @Getter('offerResponseCode', { namespace: 'lookups' }) private offerResponseCode!: (code: string|null) => string;
  @Getter('noOfferReasonCategory', { namespace: 'lookups' }) private noOfferReasonCategory!: (code: string) => string;
  @Getter('noOfferReasonCode', { namespace: 'lookups' }) private noOfferReasonCode!: (category: string, code: string) => string;
  @Getter('offerResponseReason', { namespace: 'allocations' }) private offerResponseReason!: (responseCode: string|null, responseCategoryCode: number|null, reasonCode: number|null, offerResponses: any[], organCode: number) => string|undefined;

  type = '';
  records: any[] = [];

  /**
   * Return the organ code
   *
   * Get the organ_code param from the url.
   *
   * @returns {number} organ code
   */
  get organCode(): number {
    return Number(this.$route.params.organ_code) || 0;
  }

  /**
   * Gets configuration for the vue good table configuation
   *
   * @returns {TableConfig} Offer History Table configuration
   */
  get offerHistoryTableConfig(): TableConfig {
    return {
      data: this.offerHistoryRows || [],
      columns: [
        { label: 'Ranking', field: 'effective_rank' },
        { label: this.$t('client_id').toString(), field: 'client_id', html: true },
        { label: 'Last Name', field: 'last_name' },
        { label: 'Program', field: 'program' },
        { label: (this.isNoOffer ? 'No Offer Category' : 'Response'), field: 'response' },
        { label: (this.isNoOffer ? 'No Offer Reason' : 'Reason'), field: 'reason' },
        { label: 'Date Time (EST)', field: 'date' },
      ],
      empty: 'There is no offer history',
    };
  }

  /**
   * Generate string representation of 'Reason' based on offer details
   *
   * @param {AllocationOffer} offer the recipient offer from allocation entry
   * @returns {string} description of Reason
   */
  private describeOfferReason(offer?: AllocationOffer): string {
    if (!offer) return '-';

    // TODO: need to show reason not reason category here
    let reasonValue = '';
    if (this.isNoOffer) {
      reasonValue = this.noOfferReasonCode(offer.no_offer_reason_category, offer.no_offer_reason_code) || '-';
    } else {
      reasonValue = this.offerResponseReason(offer.response_code, offer.response_reason_category_code, offer.response_reason_code, this.offerResponses, this.organCode) || '-';
    }
    return reasonValue;
  }

  /**
   * Generate string representation of 'Response' based on offer details
   *
   * @param {AllocationOffer} offer the recipient offer from allocation entry
   * @returns {string} description of Response
   */
  private describeOfferResponse(offer?: AllocationOffer): string {
    if (!offer) return '-';

    return (this.isNoOffer ? this.noOfferReasonCategory(offer.no_offer_reason_category) : this.offerResponseCode(offer.response_code)) || '-';
  }

  /**
   * Get all rows for table data
   *
   * @returns {any[]} Offer History Table configuration
   */
  get offerHistoryRows(): any[] {
    // if not an array OR there are no records
    if (!Array.isArray(this.records) || this.records.length <= 0) {
      return [];
    }
    const result: AllocationRecipient[] = [];
    this.records.forEach((recipient: AllocationRecipient) => {
      const row: any = {
        effective_rank: recipient.rank && !recipient.added_manually ? recipient.effective_rank || '-' : '-',
        client_id: recipient.client_id,
        last_name: recipient.last_name,
        program: recipient.program || '-',
        response: this.describeOfferResponse(recipient.offer),
        reason: this.describeOfferReason(recipient.offer),
        date: this.getDateTimeOffered(recipient),
      };
      result.push(row);
    });
    return result || [];
  }

  public getDateTimeOffered(row: AllocationRecipient): string|undefined {
    if (!row.offer) return undefined;

    return row.offer.datetime_offered ? this.parseDisplayDateTimeUiFromDateTime(row.offer.datetime_offered) : '-';
  }

  get isNoOffer(): boolean {
    return this.type === AllocationOfferTypeValues.NoOffer;
  }

  get isBackup(): boolean {
    return this.type === AllocationOfferTypeValues.Backup;
  }

  get getTitle(): string {
    switch(this.type) {
      case AllocationOfferTypeValues.Primary:
        return 'Primary';
        break;
      case AllocationOfferTypeValues.Backup:
        return 'Backup';
        break;
      case AllocationOfferTypeValues.NoOffer:
        return 'No';
        break;
      default:
        return '';
        break;
    }
  }

  public initializeModal(type: AllocationOfferTypeValues): void {
    this.type = type;
    this.records = [];

    switch(this.type) {
      case AllocationOfferTypeValues.Primary:
        this.records = this.primaryOfferHistory;
        break;
      case AllocationOfferTypeValues.Backup:
        this.records = this.backupOfferHistory;
        break;
      case AllocationOfferTypeValues.NoOffer:
        this.records = this.noOfferHistory;
        break;
      default:
        this.records = [];
        break;
    }

    const targetModal = this.$refs.offerHistoryModal as ModalSection;
    targetModal.toggleModal();
  }

  // Toggle modal
  private closeModal(): void {
    (this.$refs.offerHistoryModal as ModalSection).hideModal();
  }
}
